import React, { useCallback, useEffect, useState } from 'react';
import * as ReactDOM from "react-dom";
import classNames from 'class-names';
import Container from 'react-bootstrap/Container';
import Loader from 'react-loader-spinner';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Redirect, useHistory, useParams } from 'react-router-dom';

import { useBool } from '../../../../hooks/useBool';
import { useDialog } from '../../../../hooks/useDialog';

import SubscriptionCard from './SubscriptionCard';
import SignInContextProvider from '../../../../components/SignInContextProvider/SignInContextProvider';
import { SubscriptionDetailsDialog } from '../../../../components/subscriptions/SubscriptionDetailsDialog';
import { BuySubscriptionSignInDialog } from '../../../../components/subscriptions/BuySubscriptionSignInDialog';
import { BuySubscriptionSelectDateDialog } from '../../../../components/subscriptions/BuySubscriptionSelectDateDialog';
import { BuySubscriptionConfirmDialog } from '../../../../components/subscriptions/BuySubscriptionConfirmDialog';
import { BuySubscriptionSuccessDialog } from '../../../../components/subscriptions/BuySubscriptionSuccessDialog';
import { BuySubscriptionErrorDialog } from '../../../../components/subscriptions/BuySubscriptionErrorDialog';
import Notification from '../../../../components/Notifications/Notifications';

import * as SUBSCRIPTIONS_ACTIONS from '../../../../store/actions/subscriptions';
import * as AUTH_SELECTORS from '../../../../store/selectors/auth';

import styles from '../Bundles/Bundles.module.scss';

import { WIDGET_STYLE } from '../../../../const/app/WIDGET_STYLE';
import { ROUTER_URL } from '../../../../const/ROUTER_URL';
import { PAYMENT_STATUS } from '../../../../const/payment/PAYMENT_STATUS';
import { LOCALE_NAMESPACE } from '../../../../const/translations/LOCALE_NAMESPACE';
import { OpenNewWindowConfirmDialog } from '../../../../components/Common/openNewWindowConfirmDialog';

const Subscriptions = () => {
    const { t } = useTranslation(LOCALE_NAMESPACE.COMMON);
    const { t: ts } = useTranslation(LOCALE_NAMESPACE.SUBSCRIPTIONS);

    const dispatch = useDispatch();

    const { shopId, subscriptionId } = useParams();
    const history = useHistory();

    const { style } = useSelector(({ widget }) => widget);
    const {
        subscriptions,
        loading,
        paymentStatus,
        activeSubscription,
        paymentLink,
    } = useSelector(({ subscriptions }) => subscriptions);
    const { isSignup: isSignedIn } = useSelector(AUTH_SELECTORS.authSelector);
    const { subscription: showSubscriptions } = useSelector(state => state.companies.shop ? state.companies.shop.products : {});

    const [startDate, setStartDate] = useState(null)
    const [isNeedGetSubscriptionList, setIsNeedGetSubscriptionList] = useState(!isSignedIn)

    const detailsDialog = useDialog(false);
    const buySelectDateDialog = useDialog(false);
    const buyConfirmDialog = useDialog(false);
    const openNewWindowConfirmDialog = useDialog(false)
    const buySignInDialog = useDialog(false);
    const buySuccessDialog = useDialog(false);
    const buyErrorDialog = useDialog(false);

    const isSignInRedirect = useBool(false);

    useEffect(() => {
        dispatch(SUBSCRIPTIONS_ACTIONS.getSubscriptionsList());
    }, [dispatch])

    const handleOpenDetails = useCallback((subscription) => {
        dispatch(SUBSCRIPTIONS_ACTIONS.setActiveSubscription({ subscription }));
        detailsDialog.onShow();
        if (subscription.client_subscription_id) {
            dispatch(SUBSCRIPTIONS_ACTIONS.getSubscriptionDetails(subscription.client_subscription_id));
        }
        // eslint-disable-next-line
    }, [detailsDialog.onShow, dispatch]);

    const handleCloseDetails = useCallback(() => {
        dispatch(SUBSCRIPTIONS_ACTIONS.removeActiveSubscription());
        detailsDialog.onClose();
        history.push(ROUTER_URL.SHOPS_ID_SUBSCRIPTIONS({ shopId }));
        // eslint-disable-next-line
    }, [ dispatch, detailsDialog.onClose, history.push, shopId]);

    const handleBuy = useCallback(() => {
        detailsDialog.onClose();
        if (!isSignedIn) {
            isSignInRedirect.onTrue();
            return buySignInDialog.onShow();
        }
        buySelectDateDialog.onShow();
        // eslint-disable-next-line
    }, [
        detailsDialog.onClose,
        isSignedIn,
        isSignInRedirect.onTrue,
        buySignInDialog.onShow,
        buySelectDateDialog.onShow,
    ]);

    const handleCancelSignIn = useCallback(() => {
        dispatch(SUBSCRIPTIONS_ACTIONS.removeActiveSubscription());
        buySignInDialog.onClose();
        detailsDialog.onShow();
        // eslint-disable-next-line
    }, [dispatch, buySignInDialog.onClose, detailsDialog.onShow]);

    const handleCancelSelectDate = useCallback(() => {
        dispatch(SUBSCRIPTIONS_ACTIONS.removeActiveSubscription());
        buySelectDateDialog.onClose();
        detailsDialog.onShow();
        // eslint-disable-next-line
    }, [dispatch, buySelectDateDialog.onClose, detailsDialog.onShow]);

    const handleCancelBuyConfirm = useCallback(() => {
        dispatch(SUBSCRIPTIONS_ACTIONS.removeActiveSubscription());
        buyConfirmDialog.onClose();
        detailsDialog.onShow();
        // eslint-disable-next-line
    }, [dispatch, buyConfirmDialog.onClose, detailsDialog.onShow]);

    const handleBuySuccess = useCallback(() => {
        dispatch(SUBSCRIPTIONS_ACTIONS.removeActiveSubscription());
        buyConfirmDialog.onClose();
        buySuccessDialog.onShow();
        // eslint-disable-next-line
    }, [buyConfirmDialog.onClose, buySuccessDialog.onShow]);

    const handleBuyError = useCallback(() => {
        dispatch(SUBSCRIPTIONS_ACTIONS.removeActiveSubscription());
        buyConfirmDialog.onClose();
        buyErrorDialog.onShow();
        // eslint-disable-next-line
    }, [buyConfirmDialog.onClose]);

    const handleSelectDate = useCallback(({ startDate }) => {
        if (!activeSubscription?.id) {
            return;
        }

        dispatch(SUBSCRIPTIONS_ACTIONS.setConfirmText({ confirmText: null }))
        dispatch(SUBSCRIPTIONS_ACTIONS.getConfirmText({
            subscriptionId: activeSubscription.id,
            startDate: startDate.unix(),
        }))

        setStartDate(startDate)
        buySelectDateDialog.onClose();
        buyConfirmDialog.onShow();
        // eslint-disable-next-line
    }, [
        activeSubscription?.id,
        dispatch,
        buySelectDateDialog.onClose,
        detailsDialog.onShow
    ])

    const handleOpenNewWindowConfirm = useCallback(() => {
        window.open(paymentLink, '_blank');
        dispatch(SUBSCRIPTIONS_ACTIONS.setPaymentLink({ paymentLink: null }));
        openNewWindowConfirmDialog.onClose()
    }, [paymentLink, dispatch, openNewWindowConfirmDialog]);

    const handleBuyConfirm = useCallback(() => {
        if (!activeSubscription?.id) {
            return;
        }

        if (paymentLink) {
            handleOpenNewWindowConfirm()
        }

        dispatch(SUBSCRIPTIONS_ACTIONS.buySubscriptionsItem({
            subscriptionId: activeSubscription.id,
            startDate: startDate.unix(),
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        activeSubscription?.id,
        paymentLink,
        handleOpenNewWindowConfirm,
        dispatch,
        startDate,
    ]);

    useEffect(() => {
        if (paymentLink) {
            openNewWindowConfirmDialog.onShow()
        }
    // eslint-disable-next-line
    }, [paymentLink]);

    useEffect(() => {
        if (isSignInRedirect.value && isSignedIn && isNeedGetSubscriptionList) {
            dispatch(SUBSCRIPTIONS_ACTIONS.getSubscriptionsList());
            setIsNeedGetSubscriptionList(false)
        }
        // eslint-disable-next-line
    }, [
        isSignInRedirect.value,
        isSignedIn
    ]);

    useEffect(() => {
        if (isSignInRedirect.value && isSignedIn && !isNeedGetSubscriptionList) {
            const isSubscriptionBought = Boolean(
                subscriptions.find(sub => sub.id === activeSubscription.id)?.client_subscription_id
            )

            isSignInRedirect.onFalse();
            buySignInDialog.onClose();

            if (isSubscriptionBought) {
                ReactDOM.render(
                    <Notification
                        type='error'
                        error={ts('errors.subscriptionAlreadyBought')}
                    />,
                    document.getElementById('notification'),
                );
            } else {
                buySelectDateDialog.onShow();
            }
        }
        // eslint-disable-next-line
    }, [subscriptions]);

    useEffect(() => {
        if (!subscriptionId || !subscriptions?.length) {
            return;
        }
        const subscription = subscriptions.find((s) => String(s.id) === String(subscriptionId));
        if (subscription) {
            handleOpenDetails(subscription);
        }
    }, [subscriptionId, subscriptions, handleOpenDetails]);

    useEffect(() => {
        if (!activeSubscription?.id) {
          return;
        }
    
        switch (paymentStatus) {
          case PAYMENT_STATUS.OK:
            handleBuySuccess();
            break;
          case PAYMENT_STATUS.ERROR:
            handleBuyError();
            break;
          default:
            break;
        }
    }, [activeSubscription?.id, paymentStatus, handleBuySuccess, handleBuyError]);

    if (!showSubscriptions) {
        return (
            <Redirect to={ROUTER_URL.SHOPS_ID_BUNDLES({ shopId })} />
        )
    }

    return (
        <SignInContextProvider>
            <Container
                className={classNames(
                    styles.container,
                    {
                        [styles.website]: [WIDGET_STYLE.DEV, WIDGET_STYLE.WEBSITE].includes(style),
                    }
                )}
            >
                {loading ? (
                    <div>
                        <Loader
                            type="TailSpin"
                            color="#000"
                            height={25}
                            width={25}
                        />
                    </div>
                ) : (
                    subscriptions.map((subscription) => (
                        <SubscriptionCard
                            key={subscription.id}
                            subscription={subscription}
                            onSelectSubscription={handleOpenDetails}
                            t={t}
                        />
                    ))
                )}
            </Container>
            
            <SubscriptionDetailsDialog
                isVisible={detailsDialog.visible}
                subscription={activeSubscription}
                onBuy={handleBuy}
                onClose={handleCloseDetails}
            />

            <BuySubscriptionSignInDialog
                isVisible={buySignInDialog.visible}
                onClose={handleCancelSignIn}
            />

            <BuySubscriptionSelectDateDialog
                isVisible={buySelectDateDialog.visible}
                onClose={handleCancelSelectDate}
                onConfirm={handleSelectDate}
            />

            <BuySubscriptionConfirmDialog
                isVisible={buyConfirmDialog.visible}
                onClose={handleCancelBuyConfirm}
                onConfirm={handleBuyConfirm}
            />

            <OpenNewWindowConfirmDialog
                isVisible={openNewWindowConfirmDialog.visible}
                onClose={openNewWindowConfirmDialog.onClose}
                onConfirm={handleOpenNewWindowConfirm}
            />

            <BuySubscriptionSuccessDialog
                isVisible={buySuccessDialog.visible}
                onClose={buySuccessDialog.onClose}
            />

            <BuySubscriptionErrorDialog
                isVisible={buyErrorDialog.visible}
                onClose={buyErrorDialog.onClose}
            />
        </SignInContextProvider>
    )
}

export default Subscriptions
